import { ref, computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert, useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  CAPTAIN_ERROR_TYPES,
  CAPTAIN_GENERATION_FAILURE_REASONS,
} from 'dashboard/composables/captain/constants';
import {
  sendTeamComposerAction,
  sendTeamComposerFollowUp,
} from 'dashboard/api/lilithTeam';
import {
  LILITH_CONVERSATION_AI_FEATURE,
  isLilithTeamAssistAvailable,
} from 'dashboard/routes/dashboard/lilith/lilithSurfaces';

const REWRITE_ACTIONS = [
  'improve',
  'fix_spelling_grammar',
  'casual',
  'professional',
  'friendly',
  'confident',
  'straightforward',
];
const TEXT_ONLY_REWRITE_ACTIONS = REWRITE_ACTIONS.filter(
  action => action !== 'improve'
);

function getEventPrefix(action) {
  if (action === 'summarize') return 'SUMMARIZE';
  if (action === 'reply_suggestion') return 'REPLY_SUGGESTION';
  return 'REWRITE';
}

function buildPayload(action, conversationId, followUpCount = undefined) {
  const payload = { conversationId };
  if (REWRITE_ACTIONS.includes(action)) payload.operation = action;
  if (followUpCount !== undefined) payload.followUpCount = followUpCount;
  return payload;
}

function trackGenerationFailure({
  action,
  conversationId,
  followUpCount = undefined,
  stage,
  reason,
}) {
  useTrack(CAPTAIN_EVENTS.GENERATION_FAILED, {
    ...buildPayload(action, conversationId, followUpCount),
    stage,
    reason,
  });
}

function errorType(error) {
  if (
    error?.name === CAPTAIN_ERROR_TYPES.ABORT_ERROR ||
    error?.name === CAPTAIN_ERROR_TYPES.CANCELED_ERROR
  ) {
    return CAPTAIN_ERROR_TYPES.ABORTED;
  }
  if (error?.response?.status) {
    return `${CAPTAIN_ERROR_TYPES.HTTP_PREFIX}${error.response.status}`;
  }
  return CAPTAIN_ERROR_TYPES.API_ERROR;
}

export function useCopilotReply() {
  const { t } = useI18n();
  const { updateUISettings } = useUISettings();
  const { accountId } = useAccount();
  const currentChat = useMapGetter('getSelectedChat');
  const currentUser = useMapGetter('getCurrentUser');
  const employeeId = computed(() => currentUser.value?.id);
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const showEditor = ref(false);
  const isGenerating = ref(false);
  const isContentReady = ref(false);
  const generatedContent = ref('');
  const followUpContext = ref(null);
  const abortController = ref(null);

  const currentAction = ref(null);
  const followUpCount = ref(0);
  const trackedConversationId = ref(null);

  const conversationId = computed(() => currentChat.value?.id);
  const isTeamLilithAvailable = computed(() =>
    isLilithTeamAssistAvailable({
      accountEntitled: isFeatureEnabledonAccount.value(
        accountId.value,
        LILITH_CONVERSATION_AI_FEATURE
      ),
    })
  );

  const isActive = computed(() => showEditor.value || isGenerating.value);
  const isButtonDisabled = computed(
    () => isGenerating.value || !isContentReady.value
  );
  const editorTransitionKey = computed(() =>
    isActive.value ? 'copilot' : 'rich'
  );

  function showRequestError(error, fallback = '') {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      fallback ||
      t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR');
    useAlert(message);
  }

  async function processEvent(action, data, signal) {
    if (
      !accountId.value ||
      (!conversationId.value && !TEXT_ONLY_REWRITE_ACTIONS.includes(action))
    ) {
      const error = new Error(
        'Lilith editor action requires an active conversation for this action'
      );
      showRequestError(error);
      return { message: '', errorType: CAPTAIN_ERROR_TYPES.API_ERROR };
    }
    try {
      const response = await sendTeamComposerAction(
        accountId.value,
        conversationId.value,
        action,
        data || '',
        signal,
        employeeId.value
      );
      const payload = response.data || {};
      if (payload.failed) {
        const error = new Error(
          payload.error || t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR')
        );
        showRequestError(error);
        return { message: '', errorType: CAPTAIN_ERROR_TYPES.API_ERROR };
      }
      return {
        message: String(payload.message || ''),
        followUpContext: payload.follow_up_context || null,
      };
    } catch (error) {
      if (errorType(error) !== CAPTAIN_ERROR_TYPES.ABORTED) {
        showRequestError(error);
      }
      return { message: '', errorType: errorType(error) };
    }
  }

  async function followUp({ context, message, signal }) {
    if (
      !accountId.value ||
      (!conversationId.value &&
        !TEXT_ONLY_REWRITE_ACTIONS.includes(context?.action))
    ) {
      const error = new Error(
        'Lilith editor refinement requires an active conversation for this action'
      );
      showRequestError(error);
      return {
        message: '',
        followUpContext: context,
        errorType: CAPTAIN_ERROR_TYPES.API_ERROR,
      };
    }
    try {
      const response = await sendTeamComposerFollowUp(
        accountId.value,
        conversationId.value,
        context,
        message,
        signal,
        employeeId.value
      );
      const payload = response.data || {};
      if (payload.failed) {
        const error = new Error(
          payload.error || t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR')
        );
        showRequestError(error);
        return {
          message: '',
          followUpContext: context,
          errorType: CAPTAIN_ERROR_TYPES.API_ERROR,
        };
      }
      return {
        message: String(payload.message || ''),
        followUpContext: payload.follow_up_context || context,
      };
    } catch (error) {
      if (errorType(error) !== CAPTAIN_ERROR_TYPES.ABORTED) {
        showRequestError(error);
      }
      return {
        message: '',
        followUpContext: context,
        errorType: errorType(error),
      };
    }
  }

  function reset(trackDismiss = true) {
    if (trackDismiss && generatedContent.value && currentAction.value) {
      const eventKey = `${getEventPrefix(currentAction.value)}_DISMISSED`;
      useTrack(
        CAPTAIN_EVENTS[eventKey],
        buildPayload(
          currentAction.value,
          trackedConversationId.value,
          followUpCount.value
        )
      );
    }

    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
    showEditor.value = false;
    isGenerating.value = false;
    isContentReady.value = false;
    generatedContent.value = '';
    followUpContext.value = null;
    currentAction.value = null;
    followUpCount.value = 0;
    trackedConversationId.value = null;
  }

  watch([accountId, employeeId, conversationId, isTeamLilithAvailable], () =>
    reset(false)
  );

  function toggleEditor() {
    showEditor.value = !showEditor.value;
  }

  function setContentReady() {
    isContentReady.value = true;
  }

  async function execute(action, data) {
    if (!isTeamLilithAvailable.value) return;

    if (action === 'ask_copilot') {
      updateUISettings({
        is_contact_sidebar_open: false,
        is_copilot_panel_open: true,
      });
      return;
    }

    reset(false);
    const requestController = new AbortController();
    abortController.value = requestController;
    isGenerating.value = true;
    isContentReady.value = false;
    currentAction.value = action;
    followUpCount.value = 0;
    trackedConversationId.value = conversationId.value;

    try {
      const {
        message: content,
        followUpContext: newContext,
        errorType: requestErrorType,
      } = await processEvent(action, data, requestController.signal);

      if (
        requestController.signal.aborted ||
        abortController.value !== requestController
      )
        return;
      if (requestErrorType === CAPTAIN_ERROR_TYPES.ABORTED) {
        if (abortController.value === requestController) {
          isGenerating.value = false;
        }
        return;
      }

      generatedContent.value = content;
      isContentReady.value = !!content;
      followUpContext.value = newContext;
      if (content) {
        showEditor.value = true;
        const eventKey = `${getEventPrefix(action)}_USED`;
        useTrack(
          CAPTAIN_EVENTS[eventKey],
          buildPayload(action, trackedConversationId.value)
        );
      } else if (
        requestErrorType &&
        requestErrorType !== CAPTAIN_ERROR_TYPES.ABORTED
      ) {
        trackGenerationFailure({
          action,
          conversationId: trackedConversationId.value,
          stage: 'initial',
          reason: requestErrorType,
        });
      } else {
        trackGenerationFailure({
          action,
          conversationId: trackedConversationId.value,
          stage: 'initial',
          reason: CAPTAIN_GENERATION_FAILURE_REASONS.EMPTY_RESPONSE,
        });
      }
      isGenerating.value = false;
    } catch (error) {
      if (
        requestController.signal.aborted ||
        error?.name === CAPTAIN_ERROR_TYPES.ABORT_ERROR ||
        error?.name === CAPTAIN_ERROR_TYPES.CANCELED_ERROR
      ) {
        return;
      }
      trackGenerationFailure({
        action,
        conversationId: trackedConversationId.value,
        stage: 'initial',
        reason: error?.name || CAPTAIN_GENERATION_FAILURE_REASONS.EXCEPTION,
      });
      isGenerating.value = false;
    } finally {
      if (abortController.value === requestController) {
        abortController.value = null;
      }
    }
  }

  async function sendFollowUp(message) {
    if (isGenerating.value || !followUpContext.value || !message.trim()) return;

    const requestController = new AbortController();
    abortController.value = requestController;
    isGenerating.value = true;
    isContentReady.value = false;

    useTrack(CAPTAIN_EVENTS.FOLLOW_UP_SENT, {
      conversationId: trackedConversationId.value,
    });
    followUpCount.value += 1;

    try {
      const {
        message: content,
        followUpContext: updatedContext,
        errorType: requestErrorType,
      } = await followUp({
        context: followUpContext.value,
        message,
        signal: requestController.signal,
      });

      if (
        requestController.signal.aborted ||
        abortController.value !== requestController
      )
        return;
      if (requestErrorType === CAPTAIN_ERROR_TYPES.ABORTED) {
        if (abortController.value === requestController) {
          isGenerating.value = false;
        }
        return;
      }

      if (content) {
        generatedContent.value = content;
        isContentReady.value = true;
        followUpContext.value = updatedContext;
        showEditor.value = true;
      } else if (
        requestErrorType &&
        requestErrorType !== CAPTAIN_ERROR_TYPES.ABORTED
      ) {
        trackGenerationFailure({
          action: currentAction.value,
          conversationId: trackedConversationId.value,
          followUpCount: followUpCount.value,
          stage: 'follow_up',
          reason: requestErrorType,
        });
      } else {
        trackGenerationFailure({
          action: currentAction.value,
          conversationId: trackedConversationId.value,
          followUpCount: followUpCount.value,
          stage: 'follow_up',
          reason: CAPTAIN_GENERATION_FAILURE_REASONS.EMPTY_RESPONSE,
        });
      }
      isGenerating.value = false;
      if (!content) isContentReady.value = !!generatedContent.value;
    } catch (error) {
      if (
        requestController.signal.aborted ||
        error?.name === CAPTAIN_ERROR_TYPES.ABORT_ERROR ||
        error?.name === CAPTAIN_ERROR_TYPES.CANCELED_ERROR
      ) {
        return;
      }
      trackGenerationFailure({
        action: currentAction.value,
        conversationId: trackedConversationId.value,
        followUpCount: followUpCount.value,
        stage: 'follow_up',
        reason: error?.name || CAPTAIN_GENERATION_FAILURE_REASONS.EXCEPTION,
      });
      isGenerating.value = false;
      isContentReady.value = !!generatedContent.value;
    } finally {
      if (abortController.value === requestController) {
        abortController.value = null;
      }
    }
  }

  function accept() {
    if (
      !isTeamLilithAvailable.value ||
      isGenerating.value ||
      trackedConversationId.value !== conversationId.value
    )
      return '';
    const content = generatedContent.value;

    if (currentAction.value) {
      const eventKey = `${getEventPrefix(currentAction.value)}_APPLIED`;
      useTrack(
        CAPTAIN_EVENTS[eventKey],
        buildPayload(
          currentAction.value,
          trackedConversationId.value,
          followUpCount.value
        )
      );
    }

    showEditor.value = false;
    generatedContent.value = '';
    followUpContext.value = null;
    currentAction.value = null;
    followUpCount.value = 0;
    trackedConversationId.value = null;

    return content;
  }

  return {
    showEditor,
    isGenerating,
    isContentReady,
    generatedContent,
    followUpContext,
    isActive,
    isButtonDisabled,
    editorTransitionKey,
    reset,
    toggleEditor,
    setContentReady,
    execute,
    sendFollowUp,
    accept,
  };
}
